import { Children, useId, type CSSProperties, type ReactNode, type Ref } from "react";
import { cx } from "./cx";

/**
 * 紧凑编辑表：带表头的明细编辑（例如账号路由绑定），语义是 role=table，读屏按列读出表头。
 * 只有外面一层边框，表头与 .ax-table 的 th 同一套样式，行之间用分隔线，不再每项套一个卡片。
 * columns 是表头与各行共用的列宽（grid-template-columns），headers 与列一一对应（序号列、字段列……、操作列），各行同列严格对齐；
 * 宽度用令牌（--edit-type-w、--edit-target-w 等）。表头与每行各是一个 grid，列宽只能用定值或 fr，不能用 auto/max-content，否则两边按各自内容算宽、列会错开。
 * ordered 表示第一列是序号列（行用 order）：表头与单元格共用 ax-edit-order，居中对齐；操作列表头与单元格都靠右，其余列都靠左，表头与内容边缘一致。
 * 没有行时表头保留，empty（通常是 EmptyState）在表体里占满一行。
 * 表格窄于 760px（容器查询）时隐藏表头、各行退回折行布局：内含 ax-edit-grow 的单元格伸展（最少 12rem、最多 32rem），
 * 内含 ax-edit-aside 的单元格（如目标详情）落到下一行。
 */
export function EditList({ label, columns, headers, ordered = false, empty, className, children }: { label: string; columns: string; headers: ReactNode[]; ordered?: boolean; empty?: ReactNode; className?: string; children?: ReactNode }) {
  const blank = !Children.count(children);
  return (
    <div role="table" aria-label={label} className={cx("ax-edit-list", blank && "ax-edit-list-empty", className)} style={{ "--ax-edit-cols": columns } as CSSProperties}>
      <div role="rowgroup" className="ax-edit-head">
        <div role="row" className="ax-edit-grid">{headers.map((h, i) => <div key={i} role="columnheader" className={cx(ordered && i === 0 && "ax-edit-order", i === headers.length - 1 && "ax-edit-end")}>{h}</div>)}</div>
      </div>
      <div role="rowgroup" className="ax-edit-body">
        {blank ? <div role="row" className="ax-edit-row ax-edit-grid"><div role="cell" className="ax-edit-span">{empty}</div></div> : children}
      </div>
    </div>
  );
}

export interface EditRowProps {
  /** 本行的无障碍名称（如"第 2 条账号绑定"），界面上不重复显示。 */
  label: string;
  /** 行内字段，每个子元素占一列（自动包成单元格）；字段不需要各自的可见标签，表头已说明，aria-label 照常给。 */
  children: ReactNode;
  /** 行末操作列，通常是小号图标幽灵按钮（上移、下移、移除等）。 */
  actions?: ReactNode;
  /** 本行的校验提示，在本行下方占满一行，经 aria-describedby 关联到本行。 */
  error?: ReactNode;
  /** 校验提示的 id；出错字段也要关联提示时由调用方传入同一个 id（aria-describedby），不传则自动生成。 */
  errorId?: string;
  /** 序号列的可见序号（匹配顺序的直观提示）：等宽数字、次要色，aria-hidden，读屏以 label 为准。不传则没有序号列。 */
  order?: number;
  /** 挂在整行上的 ref，例如给 useFlip 做重排动画。 */
  rowRef?: Ref<HTMLDivElement>;
}

/** 紧凑编辑表的一行（role=row，带无障碍名称，可编程聚焦，便于保存失败时定位到出错行；聚焦时显示 --focus 令牌的焦点框）。 */
export function EditRow({ label, children, actions, error, errorId, order, rowRef }: EditRowProps) {
  const auto = useId(), id = errorId ?? auto;
  return (
    <div role="row" aria-label={label} aria-describedby={error ? id : undefined} tabIndex={-1} ref={rowRef} className="ax-edit-row ax-edit-grid">
      {order !== undefined && <div role="cell" className="ax-edit-order"><span aria-hidden="true">{order}</span></div>}
      {Children.map(children, c => <div role="cell" className="ax-edit-cell">{c}</div>)}
      <div role="cell" className="ax-edit-actions">{actions}</div>
      {error && <div role="cell" className="ax-edit-span"><div id={id} className="ax-field-error" role="alert">{error}</div></div>}
    </div>
  );
}
